import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Dropdown, Flex, Input, message, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/lib/table";
import type { MenuProps } from "antd";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import { ReactComponent as IconRemoveArtist } from "shared/assets/images/icon-remove-artist.svg";
import { ReactComponent as IconEditArtist } from "shared/assets/images/icon-edit-artist.svg";
import { ReactComponent as IconSwitchArtist } from "shared/assets/images/icon-switch-artist.svg";
import {
  getUserLoggedArtists,
  removeUserArtistById,
  setDefaultUserArtist,
} from "shared/service/StrmService";
import "./GerenciamentoArtistas.scss";
import { RouteComponentProps } from "react-router-dom";
import CustomLevelSvg from "shared/components/custom-level-svg/CustomLevelSvg";
import { getLoggedUser } from "explorer/reducers/users";
import { getLoggedUserArtist } from "explorer/reducers/artistas";
import { useDispatch } from "react-redux";

interface GerenciamentoArtistasSkeletonProps {
  shouldRenderSkeleton: boolean;
  skeletonRef: React.RefObject<HTMLDivElement>;
  skeletonClasses: string[];
  isLoading: boolean;
}

const GerenciamentoArtistasSkeleton = (
  props: GerenciamentoArtistasSkeletonProps
) => {
  const { shouldRenderSkeleton, skeletonRef, skeletonClasses, isLoading } =
    props;

  if (!shouldRenderSkeleton) {
    return null;
  }

  return (
    <div
      ref={skeletonRef}
      className={skeletonClasses.join(" ")}
      aria-hidden={!isLoading}
    >
      <Flex className="header">
        <Skeleton.Input
          active
          style={{ width: 350, height: 32, marginBottom: 10 }}
        />
      </Flex>
      <Divider className="divider" />
      <Flex className="search-container">
        <Skeleton.Input
          active
          style={{ width: "100%", height: 40, marginBottom: 20 }}
        />
      </Flex>
      <Flex className="artists-list">
        <Skeleton.Node
          active
          style={{ width: "100%", height: 80, marginBottom: 10 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 80, marginBottom: 10 }}
        />
        <Skeleton.Node
          active
          style={{ width: "100%", height: 80, marginBottom: 10 }}
        />
      </Flex>
    </div>
  );
};

const GerenciamentoArtistas: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [artists, setArtists] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [displaySkeleton, setDisplaySkeleton] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const skeletonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const response: any = await getUserLoggedArtists();
      const artistsData = response?.response || response || [];
      setArtists(artistsData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const isLoading = loading;
  const shouldRenderSkeleton = isLoading || displaySkeleton;

  useEffect(() => {
    if (isLoading) {
      setDisplaySkeleton(true);
    }
  }, [isLoading]);

  useEffect(() => {
    const node = skeletonRef.current;
    if (!node) {
      return;
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === "opacity" && !isLoading) {
        setDisplaySkeleton(false);
      }
    };

    node.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      node.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isLoading]);

  const filteredArtists = useMemo(() => {
    if (!searchTerm) return artists;
    const searchLower = searchTerm.toLowerCase();
    return artists.filter(
      (artist) =>
        artist.name?.toLowerCase().includes(searchLower) ||
        artist.role?.toLowerCase().includes(searchLower) ||
        artist.digitalLabel?.toLowerCase().includes(searchLower)
    );
  }, [artists, searchTerm]);

  const handleSelectArtist = async (artistId: string) => {
    try {
      await setDefaultUserArtist(artistId);
      message.success(t("pages.ArtistManagement.artistSelectedSuccessfully"));
      await fetchArtists();
      dispatch(getLoggedUser());
      dispatch(getLoggedUserArtist());
    } catch (error) {
      message.error(t("pages.ArtistManagement.errorSelectingArtist"));
    }
  };

  const handleAddArtist = () => {
    history.push("/quiz", {
      generateUserArtistId: true,
    });
  };

  const handleEditArtist = (artistId: string) => {
    history.push("/quiz", {
      id: artistId,
    });
  };

  const handleRemoveArtist = async (artistId: string) => {
    try {
      await removeUserArtistById(artistId);
      message.success(t("pages.ArtistManagement.artistRemovedSuccessfully"));
      await fetchArtists();
    } catch (error) {
      message.error(t("pages.ArtistManagement.errorRemovingArtist"));
    }
  };

  const skeletonClasses = ["content", "content--skeleton"];
  if (!isLoading) {
    skeletonClasses.push("content--stacked", "is-leaving");
  } else {
    skeletonClasses.push("is-visible");
  }

  const finalContentClasses = ["content", "content--final"];
  if (!isLoading) {
    finalContentClasses.push("is-visible");
  } else {
    finalContentClasses.push("is-hidden");
  }

  return (
    <div className="artist-management">
      <div className="content-wrapper">
        <GerenciamentoArtistasSkeleton
          shouldRenderSkeleton={shouldRenderSkeleton}
          skeletonRef={skeletonRef}
          skeletonClasses={skeletonClasses}
          isLoading={isLoading}
        />
        <div className={finalContentClasses.join(" ")} aria-hidden={isLoading}>
          <Flex className="header">
            <div className="header-content">
              <h1 className="heading-h3">
                {t("pages.ArtistManagement.title")}
              </h1>
              <h3 className="body-regular subtitle">
                {t("pages.ArtistManagement.subtitle")}
              </h3>
            </div>
            <button
              className="botao-roxo add-artist-btn"
              onClick={handleAddArtist}
              hidden={true}
            >
              {t("pages.ArtistManagement.addArtist")}
            </button>
          </Flex>
          <Flex>
            <button
              className="botao-roxo add-artist-btn"
              onClick={handleAddArtist}
            >
              {t("pages.ArtistManagement.addArtist")}
            </button>
          </Flex>
          <Divider className="divider" />
          <Flex className="search-container">
            <Input
              className="search-input"
              placeholder={t("pages.ArtistManagement.searchPlaceholder")}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
            />
          </Flex>
          <Flex className="artists-list">
            <Table
              className="artists-table"
              columns={
                [
                  {
                    title: t("pages.ArtistManagement.columnArtists"),
                    key: "artists",
                    dataIndex: "name",
                    render: (_: any, record: any) => (
                      <div className="artist-info">
                        <div className="artist-avatar">
                          {record.spotifyAvatarUrl ? (
                            <img
                              src={record.spotifyAvatarUrl}
                              alt={record.name}
                            />
                          ) : (
                            <div className="avatar-placeholder">
                              {record.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                          )}
                          {record.lastStage !== undefined &&
                            record.lastStage > 0 && (
                              <div className="level-badge">
                                <CustomLevelSvg
                                  level={record?.lastStage || 0}
                                  width={23}
                                  height={25}
                                />
                              </div>
                            )}
                        </div>
                        <div className="artist-name-container">
                          <span className="artist-name">{record.name}</span>
                          {record.isDefault && (
                            <span className="current-tag">
                              {t("pages.ArtistManagement.current")}
                            </span>
                          )}
                        </div>
                      </div>
                    ),
                  },
                  {
                    className: "actions-column",
                    title: t("pages.ArtistManagement.columnActions"),
                    key: "actions",
                    render: (_: any, record: any) => {
                      const menuItems: MenuProps["items"] = [
                        {
                          key: "switch",
                          label: t("pages.ArtistManagement.switchToArtist"),
                          icon: <IconSwitchArtist />,
                          disabled: record.isDefault,
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            if (!record.isDefault) {
                              handleSelectArtist(record.id);
                            }
                          },
                        },
                        {
                          key: "edit",
                          label: t("pages.ArtistManagement.edit"),
                          icon: <IconEditArtist />,
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            handleEditArtist(record.id);
                          },
                        },
                        {
                          key: "remove",
                          label: t("pages.ArtistManagement.remove"),
                          icon: <IconRemoveArtist />,
                          danger: true,
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            handleRemoveArtist(record.id);
                          },
                        },
                      ];

                      return (
                        <Dropdown
                          menu={{ items: menuItems }}
                          trigger={["click"]}
                          overlayClassName="artist-actions-dropdown"
                          placement="bottomRight"
                          getPopupContainer={(trigger) =>
                            trigger.parentElement || document.body
                          }
                        >
                          <button
                            className="actions-button"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <MoreOutlined />
                          </button>
                        </Dropdown>
                      );
                    },
                  },
                ] as ColumnsType
              }
              dataSource={filteredArtists}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: t("pages.ArtistManagement.noArtistsFound"),
              }}
              onRow={(record) => ({
                className: record.isDefault ? "current" : "",
              })}
            />
          </Flex>
        </div>
      </div>
    </div>
  );
};

export default GerenciamentoArtistas;
